import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { formatDateBR, formatMoneyBR, parseMoneyBR } from "../../utils/format";

type Option = { id: number; label: string };
type PartnerContribution = {
  id: number;
  type: string;
  description: string;
  amount: number;
  occurredAt: string;
};

export const PartnerTransactionsPage: React.FC = () => {
  const { id } = useParams();
  const partnerId = Number(id);
  const { client } = useAuth();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [type, setType] = useState("CONTRIBUTION");
  const [createMovement, setCreateMovement] = useState(true);
  const [bankAccountOption, setBankAccountOption] = useState<Option | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
    reload,
  } = usePagedQuery<PartnerContribution>({
    endpoint: `/modules/partners/${partnerId}/transactions`,
    filters: {},
  });

  const fetchBankAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/bank-accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; bank: { name: string }; agency: string; accountNumber: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.bank.name} ${item.agency}/${item.accountNumber}`,
      }));
    },
    [client]
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const normalizedType =
      type === "APORTE" ? "CONTRIBUTION" : type === "RETIRADA" ? "WITHDRAWAL" : type;
    const response = await client.post(
      `/modules/partners/${partnerId}/transactions`,
      {
        type: normalizedType,
        description,
        amount: parseMoneyBR(amount),
        occurredAt,
        bankAccountId: bankAccountOption?.id ?? null,
        createMovement,
      }
    );
    if (!response.ok) {
      if (response.status === 403) {
        setMessage("Erro de configuracao: entidade nao definida.");
      } else {
        setMessage("Falha ao registrar.");
      }
      return;
    }
    setDescription("");
    setAmount("");
    setOccurredAt("");
    setBankAccountOption(null);
    setCreateMovement(true);
    await reload();
  };

  const paginationText = React.useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Transacoes de socio</h1>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="mt-3">
            <label className="form-label">Busca</label>
            <input
              className="form-control"
              placeholder="Buscar por descricao"
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <PageState loading={loading} error={error}>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Descricao</th>
                        <th>Valor</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item) => (
                        <tr key={item.id}>
                          <td>{item.type}</td>
                          <td>{item.description}</td>
                          <td>{formatMoneyBR(item.amount)}</td>
                          <td>{formatDateBR(item.occurredAt)}</td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            Sem transacoes carregadas.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{paginationText}</small>
                    <div className="btn-group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled={page <= 1}
                        onClick={() => setPage(Math.max(1, page - 1))}
                      >
                        Anterior
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled={page * take >= total}
                        onClick={() => setPage(page + 1)}
                      >
                        Proximo
                      </button>
                    </div>
                  </div>
                </div>
              </PageState>
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Registrar transacao</h5>
              {message && <div className="alert alert-danger">{message}</div>}
              <form onSubmit={handleSubmit} className="d-grid gap-2">
                <div>
                  <label className="form-label">Tipo</label>
                  <select
                    className="form-select"
                    value={type}
                    onChange={(event) => setType(event.target.value)}
                  >
                    <option value="CONTRIBUTION">Aporte</option>
                    <option value="WITHDRAWAL">Retirada</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Descricao</label>
                  <input
                    className="form-control"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Valor</label>
                  <input
                    className="form-control"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Data</label>
                  <input
                    className="form-control"
                    type="date"
                    value={occurredAt}
                    onChange={(event) => setOccurredAt(event.target.value)}
                    required
                  />
                </div>
                <AutocompleteInput
                  label="Conta bancaria"
                  placeholder="Buscar conta bancaria"
                  value={bankAccountOption?.id ?? null}
                  selectedLabel={bankAccountOption?.label}
                  fetchOptions={fetchBankAccounts}
                  onSelect={setBankAccountOption}
                />
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={createMovement}
                    onChange={(event) => setCreateMovement(event.target.checked)}
                    id="createMovement"
                  />
                  <label className="form-check-label" htmlFor="createMovement">
                    Criar movimentacao automaticamente
                  </label>
                </div>
                <button className="btn btn-primary" type="submit">
                  Registrar
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
